import React from 'react';
import { useParams } from 'react-router-dom';
import Modal from 'components/modal';
import { useRecoilState } from 'recoil';
import { Box, Paper, Stack, Typography, CircularProgress } from '@mui/material';
import LabelValue from 'components/label-value';
import PreConfig from 'assets/images/PreConfig.png';
import { useMutation } from 'react-query';
import axios, { endPoints } from 'api/request';
import groupBy from 'lodash/groupBy';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccordionDetails from '@mui/material/AccordionDetails';
import Alert from 'components/alert';
import Empty from 'components/empty';
import { sspSubmitOrder, toggleQuickConfig } from 'pages/provisioning/store/event-hub';
import ProvisioningStyles from 'pages/provisioning/event-hub/style';
import Btn from 'components/button';
import { resetOrder } from './utils';

const QuickConfig = () => {
    const [loading, setLoading] = React.useState(false);
    const [products, setProducts] = React.useState({});
    const [selectedItemIndex, setSelectedItemIndex] = React.useState();
    const [isOpen, setIsopen] = useRecoilState(toggleQuickConfig);
    const [order, setOrder] = useRecoilState(sspSubmitOrder);
    const { catalogCode } = useParams();
    const quickConfigApi = useMutation((payload) => axios.post(endPoints.ssp.eventHub.quickConfig, payload));

    const handleSelect = async ({ data }) => {
        await setOrder({
            ...order,
            ...resetOrder,
        });

        setTimeout(() => {
            setOrder({
                ...order,
                environment: data?.environment || null,
                region: data?.region?.code || null,
                regionDisplayName: data?.region?.name || null,
                landingZoneDetails: { id: data?.landingZoneId },
                nsExist: 'no',
                namespaceName: null,
                performance: 'Standard',
                throughputUnits: '1',
                isAutoInflate: 'no',
                autoInflateMaxThroughputUnits: null,
                processingUnits: '1',
                cartMessage: false,
                eventHubDetails: [
                    {
                        eventHubName: '',
                        partitionCount: 1,
                        retention: null,
                        isEventHubTouched: false,
                        consumerGroupList: [{ value: '' }],
                    },
                ],
            });
            setLoading(false);
            setIsopen(false);
        }, 1000);
    };

    React.useEffect(() => {
        setSelectedItemIndex(undefined);
    }, [order?.appProfile]);

    React.useEffect(() => {
        if (order?.appProfile && order?.cloudProvider && order?.cloudService) {
            quickConfigApi.mutate({
                cloudProvider: order?.cloudProvider,
                cloudService: order?.cloudService,
                applicationName: order?.appProfile,
                catalogServiceCode: catalogCode,
            });
        }
    }, [order?.appProfile, order?.cloudProvider, order?.cloudService]);

    React.useEffect(() => {
        if (quickConfigApi?.isSuccess) {
            if (quickConfigApi?.data?.data?.length > 0) {
                const result = groupBy(quickConfigApi?.data?.data, 'environment');
                setProducts(result || {});
            }
        }
    }, [quickConfigApi?.isSuccess]);

    return (
        <Modal title="PreConfig" handleClose={() => setIsopen(false)} open={isOpen}>
            <>
                {!quickConfigApi?.isLoading && quickConfigApi?.isError && (
                    <>
                        <Empty notFound isPopup>
                            <span style={ProvisioningStyles.quickConfig.spanColor}>Something went wrong!</span>
                        </Empty>
                    </>
                )}
                {(quickConfigApi?.isLoading || quickConfigApi?.isFetching) && (
                    <Box sx={ProvisioningStyles.quickConfig.cirProgWrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.quickConfig.spanColor} />{' '}
                    </Box>
                )}

                {!quickConfigApi?.isLoading && !quickConfigApi?.isFetching && !quickConfigApi?.isError && (
                    <Stack direction="column" spacing={2}>
                        <Alert
                            title={null}
                            message="Pick one of the PreConfig options to pre-populate the configurations, then click select to save."
                            Icon={null}
                            // borderLeftColor="#A5A5A5"
                            borderLeftColor="#CCEBF9"
                            backgroundColor="#CCEBF9"
                            color="#000000"
                            ftSize="14px"
                        />{' '}
                        {Object.keys(products)?.length <= 0 && (
                            <Box
                                width="100%"
                                sx={ProvisioningStyles?.quickConfig?.emptycontainer}
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                flexDirection="column"
                            >
                                <img src={PreConfig} alt="" />
                                <Typography sx={ProvisioningStyles?.quickConfig?.emptymsg}>Sorry, there’s no PreConfig options available</Typography>
                                <Typography sx={ProvisioningStyles?.quickConfig?.emptySuggestion}>Please manually fill out the provisioning options</Typography>
                            </Box>
                        )}
                        {Object.keys(products)?.length > 0 && (
                            <>
                                {Object.keys(products).map((key, index) => (
                                    <Accordion defaultExpanded disableGutters sx={ProvisioningStyles.quickConfig.wrapper} key={`${key}-${index.toString()}`}>
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon sx={{ color: (theme) => theme.palette.textColors.$color_text_primary }} />}
                                            sx={ProvisioningStyles.quickConfig.envTitle}
                                        >
                                            <Typography sx={ProvisioningStyles.quickConfig.title}>{`${key} Environment`}</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails sx={ProvisioningStyles.quickConfig.content}>
                                            {products[key].map((prod, indexPro) => (
                                                <Paper
                                                    sx={
                                                        selectedItemIndex === `${index.toString()}-${indexPro.toString()}`
                                                            ? ProvisioningStyles.quickConfig.selectedPaper
                                                            : ProvisioningStyles.quickConfig.paper
                                                    }
                                                    key={`${index.toString()}-${indexPro.toString()}`}
                                                    style={ProvisioningStyles.quickConfig.labeledPaper}
                                                    variant="outlined"
                                                    elevation={0}
                                                    square
                                                >
                                                    <Stack p={1} className="preconfig-details">
                                                        <LabelValue sx={ProvisioningStyles.quickConfig.labelValue} label="Region" value={prod?.region?.name} />
                                                        <LabelValue sx={ProvisioningStyles.quickConfig.labelValue} label="Landing Zone" value={prod?.label} />
                                                        <LabelValue
                                                            sx={ProvisioningStyles.quickConfig.labelValue}
                                                            label="Subscription"
                                                            value={prod?.subscription}
                                                        />
                                                        <LabelValue
                                                            sx={ProvisioningStyles.quickConfig.labelValue}
                                                            label="Resource Group"
                                                            value={prod?.resourceGroup}
                                                        />
                                                        <LabelValue
                                                            sx={ProvisioningStyles.quickConfig.labelValue}
                                                            label="Data Classification"
                                                            value={prod?.dataClassification}
                                                        />
                                                    </Stack>
                                                    <Box sx={ProvisioningStyles.quickConfig.selectBtn}>
                                                        <Btn
                                                            className="custom-btn"
                                                            size="small"
                                                            variant="contained"
                                                            color={`${
                                                                selectedItemIndex !== `${index.toString()}-${indexPro.toString()}`
                                                                    ? 'cmpPrimary'
                                                                    : 'cmpSelected'
                                                            }`}
                                                            onClick={() => {
                                                                setLoading(true);
                                                                handleSelect({ data: prod });
                                                                setSelectedItemIndex(`${index.toString()}-${indexPro.toString()}`);
                                                            }}
                                                            disabled={selectedItemIndex === `${index.toString()}-${indexPro.toString()}`}
                                                            sx={ProvisioningStyles.quickConfig.selectedBtn}
                                                            data-testid="select-btn"
                                                        >
                                                            {selectedItemIndex !== `${index.toString()}-${indexPro.toString()}` && !loading ? 'Select' : false}
                                                            {selectedItemIndex !== `${index.toString()}-${indexPro.toString()}` && loading ? 'Select' : false}
                                                            {selectedItemIndex === `${index.toString()}-${indexPro.toString()}` && !loading
                                                                ? 'Selected'
                                                                : false}
                                                            {selectedItemIndex === `${index.toString()}-${indexPro.toString()}` && loading
                                                                ? 'Selecting'
                                                                : false}
                                                        </Btn>
                                                    </Box>
                                                </Paper>
                                            ))}
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </>
                        )}
                    </Stack>
                )}
            </>
        </Modal>
    );
};

export default React.memo(QuickConfig);
